if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          dots: this.querySelector('.media-dots')
        };

        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.viewer) return;

        this.elements.viewer.addEventListener(
          'slideChanged',
          debounce(this.onSlideChanged.bind(this), 300)
        );

        if (this.elements.thumbnails) {
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
            mediaToSwitch
              .querySelector('button')
              .addEventListener(
                'click',
                this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false)
              );
          });
        }

        // Build dots ONLY on mobile
        if (!this.mql.matches && this.elements.dots) {
          this.buildDots();
        }

        // Remove list semantic for desktop thumbnail layout
        if (this.dataset.desktopLayout?.includes('thumbnail') && this.mql.matches) {
          this.removeListSemantic();
        }
      }

      /* ----------------------------
         SLIDE CHANGE
      -----------------------------*/
      onSlideChanged(event) {
        const mediaId = event.detail.currentElement.dataset.mediaId;

        if (this.elements.thumbnails) {
          const thumbnail = this.elements.thumbnails.querySelector(
            `[data-target="${mediaId}"]`
          );
          this.setActiveThumbnail(thumbnail);
        }

        this.syncActiveDot(mediaId);
      }

      /* ----------------------------
         SET ACTIVE MEDIA
      -----------------------------*/
      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');

        if (!activeMedia) return;

        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((el) => {
          el.classList.remove('is-active');
        });

        activeMedia.classList.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia &&
            activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(
              `[data-target="${mediaId}"]`
            );
            activeThumbnail?.parentElement.firstChild !== activeThumbnail &&
              activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) {
            this.elements.viewer.resetPages();
          }
        }

        this.preventStickyHeader();

        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({
              left: activeMedia.offsetLeft
            });
          }

          const rect = activeMedia.getBoundingClientRect();
          if (rect.top < -0.5) {
            window.scrollTo({
              top: rect.top + window.scrollY,
              behavior: 'smooth'
            });
          }
        });

        this.playActiveMedia(activeMedia);

        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(
            `[data-target="${mediaId}"]`
          );
          this.setActiveThumbnail(activeThumbnail);
          this.announceLiveRegion(activeMedia, activeThumbnail?.dataset.mediaPosition);
        }

        this.syncActiveDot(mediaId);
      }

      /* ----------------------------
         THUMBNAILS
      -----------------------------*/
      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((btn) => btn.removeAttribute('aria-current'));

        thumbnail.querySelector('button')?.setAttribute('aria-current', true);

        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({
          left: thumbnail.offsetLeft
        });
      }

      /* ----------------------------
         DOTS
      -----------------------------*/
      buildDots() {
        if (!this.elements.dots || !this.elements.viewer) return;

        const mediaItems = this.elements.viewer.querySelectorAll('[data-media-id]');
        if (!mediaItems.length) return;

        const uniqueMediaMap = new Map();

        mediaItems.forEach((media) => {
          let mediaId = media.dataset.mediaId;

          // Normalize ID (remove template / section prefix)
          if (mediaId.includes('-')) {
            mediaId = mediaId.split('-').pop();
          }

          // Store only one entry per real media ID
          if (!uniqueMediaMap.has(mediaId)) {
            uniqueMediaMap.set(mediaId, media.dataset.mediaId);
          }
        });

        this.elements.dots.innerHTML = '';
        this.elements.dots.hidden = false;

        let index = 0;

        uniqueMediaMap.forEach((originalMediaId) => {
          const button = document.createElement('button');
          button.className = 'media-dot';
          button.dataset.target = originalMediaId;
          button.setAttribute('aria-label', `Go to slide ${index + 1}`);

          button.addEventListener('click', () => {
            this.setActiveMedia(originalMediaId, false);
          });

          this.elements.dots.appendChild(button);
          index++;
        });

        this.syncActiveDot();
      }

      syncActiveDot(activeMediaId = null) {
        if (!this.elements.dots) return;

        const currentId =
          activeMediaId ||
          this.elements.viewer.querySelector('.is-active')?.dataset.mediaId;

        this.elements.dots.querySelectorAll('.media-dot').forEach((dot) => {
          dot.classList.toggle('is-active', dot.dataset.target === currentId);
        });
      }

      /* ----------------------------
         ACCESSIBILITY & MEDIA
      -----------------------------*/
      announceLiveRegion(activeItem, position) {
        if (!activeItem || !position) return;

        const image = activeItem.querySelector('img');
        if (!image) return;

        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML =
            window.accessibilityStrings.imageAvailable.replace('[index]', position);

          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };

        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        deferredMedia?.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader =
          this.stickyHeader || document.querySelector('sticky-header');

        this.stickyHeader?.dispatchEvent(
          new Event('preventHeaderReveal')
        );
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;

        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) =>
          slide.setAttribute('role', 'presentation')
        );
      }
    }
  );
}
